import { useSearchParams } from "react-router-dom";

export enum SearchParams {
  Option = "option",
  SearchValue = "q",
  Tag = "tag",
  Country = "country",
  VenueOnlyVisible = "venueOnlyVisible",
  ShopId = "shopId",
  FromDate = "from",
  ToDate = "to",
  Tags = "tags",
  Categories = "categories",
  KitchenCategories = "kitchenCategories",
  Currency = "curr",
  Classification = "classification",
}

export const useSearchParamsManipulation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const _option = searchParams.get(SearchParams.Option);
  const _searchValue = searchParams.get(SearchParams.SearchValue);
  const _tag = searchParams.get(SearchParams.Tag);
  const _country = searchParams.get(SearchParams.Country);
  const _venueOnlyVisible = searchParams.get(SearchParams.VenueOnlyVisible);
  const _shopId = searchParams.get(SearchParams.ShopId);
  const _fromDate = searchParams.get(SearchParams.FromDate);
  const _toDate = searchParams.get(SearchParams.ToDate);
  const _tags = searchParams.get(SearchParams.Tags);
  const _categories = searchParams.get(SearchParams.Categories);
  const _kitchenCategories = searchParams.get(SearchParams.KitchenCategories);
  const _currency = searchParams.get(SearchParams.Currency);
  const currency = _currency ? decodeURIComponent(_currency) : null;
  const _classification = searchParams.get(SearchParams.Classification);
  const option = _option ? decodeURIComponent(_option) : null;
  const searchValue = _searchValue ? decodeURIComponent(_searchValue) : null;
  const tag = _tag ? decodeURIComponent(_tag) : null;
  const country = _country ? decodeURIComponent(_country) : null;
  const venueOnlyVisible = _venueOnlyVisible
    ? decodeURIComponent(_venueOnlyVisible)
    : null;
  const shopId = _shopId ? decodeURIComponent(_shopId) : null;
  const fromDate = _fromDate ? decodeURIComponent(_fromDate) : null;
  const toDate = _toDate ? decodeURIComponent(_toDate) : null;
  const tags = _tags ? decodeURIComponent(_tags) : null;
  const categories = _categories ? decodeURIComponent(_categories) : null;
  const kitchenCategories = _kitchenCategories
    ? decodeURIComponent(_kitchenCategories)
    : null;
  const classification = _classification
    ? decodeURIComponent(_classification)
    : null;

  const setKitchenCategories = (option: string[]) => {
    setSearchParams((prev) => {
      prev.set(
        SearchParams.KitchenCategories,
        encodeURIComponent(option.join(","))
      );
      return prev;
    });
  };

  const setClassification = (value: string[]) => {
    setSearchParams((prev) => {
      prev.set(
        SearchParams.Classification,
        encodeURIComponent(value.join(","))
      );
      return prev;
    });
  };

  const removeKitchenCategories = () => {
    searchParams.delete(SearchParams.KitchenCategories);
    setSearchParams(searchParams);
  };

  const setCurrency = (option: string[]) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Currency, encodeURIComponent(option.join(",")));
      return prev;
    });
  };

  const removeCurrency = () => {
    searchParams.delete(SearchParams.Currency);
    setSearchParams(searchParams);
  };

  const removeClassification = () => {
    searchParams.delete(SearchParams.Classification);
    setSearchParams(searchParams);
  };

  const setOption = (option: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Option, encodeURIComponent(option));
      return prev;
    });
  };

  const removeOption = () => {
    searchParams.delete(SearchParams.Option);
    setSearchParams(searchParams);
  };

  const setSearchValue = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.SearchValue, encodeURIComponent(value));
      return prev;
    });
  };

  const removeSearchValue = () => {
    searchParams.delete(SearchParams.SearchValue);
    setSearchParams(searchParams);
  };

  const setTag = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Tag, encodeURIComponent(value));
      return prev;
    });
  };

  const removeTag = () => {
    searchParams.delete(SearchParams.Tag);
    setSearchParams(searchParams);
  };

  const setCountry = (value: string[]) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Country, encodeURIComponent(value.join(",")));
      return prev;
    });
  };

  const removeCountry = () => {
    searchParams.delete(SearchParams.Country);
    setSearchParams(searchParams);
  };

  const setVenueOnlyVisible = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.VenueOnlyVisible, encodeURIComponent(value));
      return prev;
    });
  };

  const removeVenueOnlyVisible = () => {
    searchParams.delete(SearchParams.VenueOnlyVisible);
    setSearchParams(searchParams);
  };

  const setShopId = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.ShopId, encodeURIComponent(value));
      return prev;
    });
  };

  const removeShopId = () => {
    searchParams.delete(SearchParams.ShopId);
    setSearchParams(searchParams);
  };

  const setFromDate = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.FromDate, encodeURIComponent(value));
      return prev;
    });
  };

  const removeFromDate = () => {
    searchParams.delete(SearchParams.FromDate);
    setSearchParams(searchParams);
  };

  const setToDate = (value: string) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.ToDate, encodeURIComponent(value));
      return prev;
    });
  };

  const removeToDate = () => {
    searchParams.delete(SearchParams.ToDate);
    setSearchParams(searchParams);
  };

  const setTags = (value: string[]) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Tags, encodeURIComponent(value.join(",")));
      return prev;
    });
  };

  const removeTags = () => {
    searchParams.delete(SearchParams.Tags);
    setSearchParams(searchParams);
  };

  const setCategories = (value: string[]) => {
    setSearchParams((prev) => {
      prev.set(SearchParams.Categories, encodeURIComponent(value.join(",")));
      return prev;
    });
  };

  const removeCategories = () => {
    searchParams.delete(SearchParams.Categories);
    setSearchParams(searchParams);
  };

  return {
    option,
    searchValue,
    setOption,
    removeOption,
    setSearchValue,
    removeSearchValue,
    tag,
    country,
    venueOnlyVisible,
    setTag,
    removeTag,
    setCountry,
    removeCountry,
    setVenueOnlyVisible,
    removeVenueOnlyVisible,
    setShopId,
    removeShopId,
    setFromDate,
    removeFromDate,
    setToDate,
    removeToDate,
    fromDate,
    toDate,
    shopId,
    tags,
    categories,
    kitchenCategories,
    setTags,
    removeTags,
    setCategories,
    removeCategories,
    setKitchenCategories,
    removeKitchenCategories,
    setCurrency,
    removeCurrency,
    currency,
    classification,
    setClassification,
    removeClassification,
  };
};
